import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { 
  Volume2, VolumeX, Languages, X, Mic, MicOff, 
  Play, Pause, Settings, Headphones, BookOpen,
  ChevronDown, Loader2, Power, StopCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { signLanguageEventBus } from '../utils/signLanguageSync';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Language options with names
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'ht', name: 'Kreyòl', flag: '🇭🇹' },
];

// Cute Robot Component
const CuteRobot = ({ mood, size = 'large', isAutoReadOn = false }) => {
  const isSmall = size === 'small';
  
  return (
    <motion.div 
      className="relative"
      style={{ width: isSmall ? 64 : 100, height: isSmall ? 70 : 110 }}
      animate={mood === 'speaking' ? { y: [0, -3, 0] } : {}}
      transition={{ repeat: Infinity, duration: 0.5 }}
    >
      {/* Glow effect at bottom - green when auto-read is on */}
      <div 
        className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full blur-md"
        style={{
          width: isSmall ? 30 : 50,
          height: isSmall ? 8 : 12,
          background: isAutoReadOn 
            ? 'radial-gradient(ellipse, rgba(0, 255, 100, 0.8), transparent)'
            : 'radial-gradient(ellipse, rgba(0, 255, 255, 0.6), transparent)'
        }}
      />
      
      {/* Body */}
      <div 
        className="absolute bottom-2 left-1/2 -translate-x-1/2"
        style={{
          width: isSmall ? 36 : 60,
          height: isSmall ? 42 : 70,
          background: isAutoReadOn
            ? 'linear-gradient(180deg, #E8F8E8 0%, #B8E3B8 50%, #9BD89B 100%)'
            : 'linear-gradient(180deg, #E8F4F8 0%, #B8D4E3 50%, #9BC4D8 100%)',
          borderRadius: '45% 45% 50% 50%',
          boxShadow: 'inset -5px -5px 15px rgba(255,255,255,0.5), inset 5px 5px 15px rgba(0,0,0,0.1)'
        }}
      >
        {/* Body accent line */}
        <div 
          className="absolute top-2 left-1/2 -translate-x-1/2"
          style={{
            width: '60%',
            height: 3,
            background: isAutoReadOn
              ? 'linear-gradient(90deg, transparent, rgba(0, 255, 100, 0.7), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.5), transparent)',
            borderRadius: 2
          }}
        />
      </div>
      
      {/* Head */}
      <div 
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          top: isSmall ? 0 : 0,
          width: isSmall ? 44 : 72,
          height: isSmall ? 36 : 58,
          background: 'linear-gradient(180deg, #F0F5F7 0%, #D8E8F0 100%)',
          borderRadius: '50% 50% 45% 45%',
          boxShadow: '0 4px 15px rgba(0,0,0,0.15), inset -3px -3px 10px rgba(255,255,255,0.8)'
        }}
      >
        {/* Face screen */}
        <div 
          className="absolute left-1/2 -translate-x-1/2 overflow-hidden"
          style={{
            top: isSmall ? 4 : 6,
            width: isSmall ? 34 : 56,
            height: isSmall ? 24 : 40,
            background: 'linear-gradient(180deg, #1a1a2e 0%, #2d2d44 100%)',
            borderRadius: '40% 40% 45% 45%',
            border: '2px solid #c0d0e0'
          }}
        >
          {/* Eyes */}
          <div className="flex justify-center gap-2 pt-1" style={{ paddingTop: isSmall ? 4 : 8 }}>
            <motion.div 
              style={{
                width: isSmall ? 8 : 14,
                height: isSmall ? 10 : 16,
                background: isAutoReadOn
                  ? 'radial-gradient(ellipse, #00ff64 30%, #00cc50 100%)'
                  : 'radial-gradient(ellipse, #00ffff 30%, #00cccc 100%)',
                borderRadius: '50%',
                boxShadow: isAutoReadOn
                  ? '0 0 10px #00ff64, 0 0 20px rgba(0, 255, 100, 0.5)'
                  : '0 0 10px #00ffff, 0 0 20px rgba(0, 255, 255, 0.5)'
              }}
              animate={mood === 'listening' ? { scale: [1, 1.2, 1] } : mood === 'thinking' ? { opacity: [1, 0.5, 1] } : {}}
              transition={{ repeat: Infinity, duration: 0.8 }}
            />
            <motion.div 
              style={{
                width: isSmall ? 8 : 14,
                height: isSmall ? 10 : 16,
                background: isAutoReadOn
                  ? 'radial-gradient(ellipse, #00ff64 30%, #00cc50 100%)'
                  : 'radial-gradient(ellipse, #00ffff 30%, #00cccc 100%)',
                borderRadius: '50%',
                boxShadow: isAutoReadOn
                  ? '0 0 10px #00ff64, 0 0 20px rgba(0, 255, 100, 0.5)'
                  : '0 0 10px #00ffff, 0 0 20px rgba(0, 255, 255, 0.5)'
              }}
              animate={mood === 'listening' ? { scale: [1, 1.2, 1] } : mood === 'thinking' ? { opacity: [1, 0.5, 1] } : {}}
              transition={{ repeat: Infinity, duration: 0.8, delay: 0.1 }}
            />
          </div>
          
          {/* Mouth */}
          <motion.div 
            className="mx-auto mt-1"
            style={{
              width: isSmall ? 6 : 10,
              height: isSmall ? 4 : 6,
              background: isAutoReadOn ? '#00ff64' : '#00ffff',
              borderRadius: '0 0 50% 50%',
              boxShadow: isAutoReadOn
                ? '0 0 8px rgba(0, 255, 100, 0.6)'
                : '0 0 8px rgba(0, 255, 255, 0.6)',
              marginTop: isSmall ? 2 : 4
            }}
            animate={mood === 'speaking' ? { scaleY: [1, 1.5, 0.5, 1] } : {}}
            transition={{ repeat: Infinity, duration: 0.3 }}
          />
        </div>
        
        {/* Headset - Left */}
        <div 
          className="absolute"
          style={{
            left: isSmall ? -4 : -6,
            top: isSmall ? 10 : 16,
            width: isSmall ? 10 : 16,
            height: isSmall ? 14 : 22,
            background: 'linear-gradient(90deg, #d0e0e8, #f0f5f7)',
            borderRadius: '40% 30% 30% 40%',
            boxShadow: '-2px 2px 5px rgba(0,0,0,0.15)'
          }}
        />
        
        {/* Headset - Right */}
        <div 
          className="absolute"
          style={{
            right: isSmall ? -4 : -6,
            top: isSmall ? 10 : 16,
            width: isSmall ? 10 : 16,
            height: isSmall ? 14 : 22,
            background: 'linear-gradient(270deg, #d0e0e8, #f0f5f7)',
            borderRadius: '30% 40% 40% 30%',
            boxShadow: '2px 2px 5px rgba(0,0,0,0.15)'
          }}
        />
        
        {/* Headset band */}
        <div 
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            top: isSmall ? -2 : -3,
            width: isSmall ? 8 : 12,
            height: isSmall ? 4 : 6,
            background: '#2d2d44',
            borderRadius: '50% 50% 0 0'
          }}
        />
        
        {/* Microphone */}
        <div 
          className="absolute"
          style={{
            left: isSmall ? -8 : -14,
            top: isSmall ? 18 : 28,
            width: isSmall ? 12 : 20,
            height: isSmall ? 2 : 3,
            background: '#1a1a2e',
            borderRadius: 2,
            transformOrigin: 'right center',
            transform: 'rotate(-20deg)'
          }}
        >
          <div 
            className="absolute left-0 top-1/2 -translate-y-1/2"
            style={{
              width: isSmall ? 4 : 6,
              height: isSmall ? 4 : 6,
              background: '#1a1a2e',
              borderRadius: '50%'
            }}
          />
        </div>
      </div>
      
      {/* Left arm */}
      <motion.div 
        className="absolute"
        style={{
          left: isSmall ? 4 : 6,
          top: isSmall ? 38 : 62,
          width: isSmall ? 8 : 14,
          height: isSmall ? 16 : 26,
          background: isAutoReadOn
            ? 'linear-gradient(180deg, #E8F8E8, #B8E3B8)'
            : 'linear-gradient(180deg, #E8F4F8, #B8D4E3)',
          borderRadius: '40%',
          transformOrigin: 'top center'
        }}
        animate={mood === 'happy' ? { rotate: [0, -10, 0] } : {}}
        transition={{ repeat: Infinity, duration: 2 }}
      />
      
      {/* Right arm */}
      <motion.div 
        className="absolute"
        style={{
          right: isSmall ? 4 : 6,
          top: isSmall ? 38 : 62,
          width: isSmall ? 8 : 14,
          height: isSmall ? 16 : 26,
          background: isAutoReadOn
            ? 'linear-gradient(180deg, #E8F8E8, #B8E3B8)'
            : 'linear-gradient(180deg, #E8F4F8, #B8D4E3)',
          borderRadius: '40%',
          transformOrigin: 'top center'
        }}
        animate={mood === 'happy' ? { rotate: [0, 10, 0] } : mood === 'speaking' ? { rotate: [0, 15, 0, -15, 0] } : {}}
        transition={{ repeat: Infinity, duration: mood === 'speaking' ? 0.5 : 2, delay: 0.5 }}
      />
      
      {/* Auto-read indicator */}
      {isAutoReadOn && (
        <motion.div
          className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ repeat: Infinity, duration: 1 }}
        />
      )}
    </motion.div>
  );
};

export default function TranslatorBot() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('translate');
  const [targetLanguage, setTargetLanguage] = useState(() => {
    const saved = localStorage.getItem('translatorLang');
    if (saved && LANGUAGES.find(l => l.code === saved)) {
      return saved;
    }
    // Try to match current i18n language
    const currentLang = i18n.language?.split('-')[0];
    const match = LANGUAGES.find(l => l.code === i18n.language || l.code === currentLang);
    return match ? match.code : 'en';
  });
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [inputText, setInputText] = useState('');
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState(() => {
    return parseFloat(localStorage.getItem('translatorSpeed') || '1.0');
  });
  const [botMood, setBotMood] = useState('happy');
  
  // Auto-read mode state
  const [isAutoReadOn, setIsAutoReadOn] = useState(() => {
    return localStorage.getItem('autoReadMode') === 'true';
  });
  const [autoReadStatus, setAutoReadStatus] = useState('idle'); // idle, reading, paused
  
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('translatorLang', targetLanguage);
  }, [targetLanguage]);

  useEffect(() => {
    localStorage.setItem('translatorSpeed', audioSpeed.toString());
  }, [audioSpeed]);

  useEffect(() => {
    localStorage.setItem('autoReadMode', isAutoReadOn.toString());
  }, [isAutoReadOn]);

  // Extract readable content from page
  const extractPageContent = useCallback(() => {
    // Get main content areas
    const selectors = [
      'main',
      'article',
      '[data-testid*="page"]',
      '.prose',
      'section h1, section h2, section h3, section p',
      'h1, h2, h3',
      'p'
    ];
    
    let content = '';
    
    // Try to get main content
    const main = document.querySelector('main') || document.querySelector('article');
    if (main) {
      // Get headings and paragraphs
      const elements = main.querySelectorAll('h1, h2, h3, h4, p, li');
      elements.forEach(el => {
        const text = el.textContent?.trim();
        if (text && text.length > 10) {
          content += text + '. ';
        }
      });
    }
    
    // If no main content, try sections
    if (!content) {
      const sections = document.querySelectorAll('section');
      sections.forEach(section => {
        const elements = section.querySelectorAll('h1, h2, h3, h4, p');
        elements.forEach(el => {
          const text = el.textContent?.trim();
          if (text && text.length > 10) {
            content += text + '. ';
          }
        });
      });
    }
    
    // Limit content length
    return content.substring(0, 3000);
  }, []);

  // Auto-read page content
  const autoReadPage = useCallback(async () => {
    if (!isAutoReadOn) return;
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const content = extractPageContent();
    if (!content) {
      console.log('No readable content found on this page');
      return;
    }
    
    setAutoReadStatus('reading');
    setBotMood('thinking');
    
    try {
      const response = await axios.post(`${API}/translator/translate-and-speak`, {
        text: content,
        target_language: targetLanguage,
        source_language: 'en'
      }, {
        signal: abortControllerRef.current.signal
      });
      
      // Check if still in auto-read mode before playing
      if (response.data.audio_base64) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio_base64}`;
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.playbackRate = audioSpeed;
          await audioRef.current.play();
          setIsPlaying(true);
          setBotMood('speaking');
          
          // Trigger sign language avatar to sign the same content
          signLanguageEventBus.emitSignText(content);
        }
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Auto-read error:', error);
      }
      setBotMood('happy');
      setAutoReadStatus('idle');
    }
  }, [isAutoReadOn, targetLanguage, audioSpeed, extractPageContent]);

  // Listen for page changes when auto-read is on
  useEffect(() => {
    if (isAutoReadOn) {
      // Stop current audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsPlaying(false);
      setAutoReadStatus('idle');
      
      // Small delay to let page content load, then start reading
      const timer = setTimeout(() => {
        autoReadPage();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isAutoReadOn]);

  // Re-read page when language changes while auto-read is on
  useEffect(() => {
    if (isAutoReadOn) {
      // Stop current audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsPlaying(false);
      setAutoReadStatus('idle');
      
      // Show notification
      const langName = LANGUAGES.find(l => l.code === targetLanguage)?.name || targetLanguage;
      toast.success(`Switching to ${langName}...`);
      
      // Re-read with new language
      const timer = setTimeout(() => {
        autoReadPage();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [targetLanguage]);

  // Toggle auto-read mode
  const toggleAutoRead = () => {
    if (isAutoReadOn) {
      // Turn off
      setIsAutoReadOn(false);
      setAutoReadStatus('idle');
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsPlaying(false);
      setBotMood('happy');
      toast.success('Auto-read mode OFF');
    } else {
      // Turn on
      setIsAutoReadOn(true);
      toast.success('Auto-read mode ON - I\'ll read every page for you!');
      // Start reading current page
      setTimeout(() => autoReadPage(), 500);
    }
  };

  // Stop current reading
  const stopReading = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsPlaying(false);
    setAutoReadStatus('idle');
    setBotMood('happy');
    
    // Stop sign language avatar too
    signLanguageEventBus.emitStopSigning();
  };

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInputText(transcript);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setBotMood('happy');
      };
    }
  }, []);

  // Toggle speech recognition
  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setBotMood('happy');
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
      setBotMood('listening');
    }
  };

  // Translate and speak
  const translateAndSpeak = async () => {
    if (!inputText.trim()) {
      toast.error('Please enter or speak some text first');
      return;
    }
    
    setIsProcessing(true);
    setBotMood('thinking');
    
    try {
      const response = await axios.post(`${API}/translator/translate-and-speak`, {
        text: inputText,
        target_language: targetLanguage,
        source_language: 'en'
      });
      
      setTranslatedText(response.data.translated_text);
      
      if (response.data.audio_base64) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio_base64}`;
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.playbackRate = audioSpeed;
          await audioRef.current.play();
          setIsPlaying(true);
          setBotMood('speaking');
        }
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Translation failed. Please try again.');
      setBotMood('happy');
    } finally {
      setIsProcessing(false);
    }
  };

  // Read aloud any text
  const readAloud = async (text) => {
    if (!text?.trim()) {
      toast.error('No text to read');
      return;
    }
    
    setIsProcessing(true);
    setBotMood('thinking');
    
    try {
      const response = await axios.post(`${API}/translator/read-aloud`, {
        text: text,
        language: targetLanguage,
        speed: audioSpeed
      });
      
      if (response.data.audio_base64) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio_base64}`;
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.playbackRate = audioSpeed;
          await audioRef.current.play();
          setIsPlaying(true);
          setBotMood('speaking');
        }
      }
    } catch (error) {
      console.error('Read aloud error:', error);
      toast.error('Failed to read text. Please try again.');
      setBotMood('happy');
    } finally {
      setIsProcessing(false);
    }
  };

  // Read selected text from page
  const readSelectedText = () => {
    const selectedText = window.getSelection()?.toString();
    if (selectedText) {
      readAloud(selectedText);
    } else {
      toast.info('Select some text on the page first, then click Read Aloud');
    }
  };

  // Audio ended handler
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAutoReadStatus('idle');
    setBotMood('happy');
  };

  // Toggle play/pause
  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        setBotMood('happy');
      } else if (audioRef.current.src) {
        audioRef.current.play();
        setIsPlaying(true);
        setBotMood('speaking');
      }
    }
  };

  return (
    <>
      {/* Hidden audio element */}
      <audio ref={audioRef} onEnded={handleAudioEnded} />
      
      {/* Floating Bot Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 1 }}
      >
        <AnimatePresence>
          {!isOpen && (
            <motion.button
              onClick={() => setIsOpen(true)}
              className="relative group"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="translator-bot-button"
            >
              <CuteRobot mood={botMood} size="small" isAutoReadOn={isAutoReadOn} />
              
              {/* Sound waves when speaking */}
              {isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className={`absolute w-20 h-20 border-2 ${isAutoReadOn ? 'border-green-400/30' : 'border-cyan-400/30'} rounded-full animate-ping`} />
                  <div className={`absolute w-24 h-24 border ${isAutoReadOn ? 'border-green-400/20' : 'border-cyan-400/20'} rounded-full animate-ping`} style={{ animationDelay: '0.2s' }} />
                </div>
              )}
              
              {/* Tooltip */}
              <div className={`absolute bottom-full right-0 mb-2 px-3 py-1.5 ${isAutoReadOn ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-purple to-burgundy'} text-white text-xs rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg`}>
                {isAutoReadOn ? '🔊 Auto-Reading ON' : '🌐 Translator Bot'}
              </div>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Expanded Panel */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl border border-gold/20 overflow-hidden w-80"
              data-testid="translator-panel"
            >
              {/* Header with Robot */}
              <div className={`${isAutoReadOn ? 'bg-gradient-to-r from-green-600 to-green-700' : 'bg-gradient-to-r from-[#1a1a2e] to-[#2d2d44]'} p-4 text-white relative`}>
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X size={18} />
                </button>
                
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <CuteRobot mood={botMood} size="small" isAutoReadOn={isAutoReadOn} />
                  </div>
                  
                  <div>
                    <h3 className={`font-serif text-lg ${isAutoReadOn ? 'text-white' : 'text-cyan-300'}`}>Translator Bot</h3>
                    <p className="text-white/70 text-xs">
                      {isAutoReadOn && autoReadStatus === 'reading' && '🔊 Reading page...'}
                      {isAutoReadOn && autoReadStatus !== 'reading' && '✅ Auto-read ON'}
                      {!isAutoReadOn && botMood === 'thinking' && '🤔 Translating...'}
                      {!isAutoReadOn && botMood === 'speaking' && '🔊 Speaking...'}
                      {!isAutoReadOn && botMood === 'listening' && '👂 Listening...'}
                      {!isAutoReadOn && botMood === 'happy' && '✨ Ready to help!'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Auto-Read Toggle Bar */}
              <div className={`px-4 py-3 ${isAutoReadOn ? 'bg-green-50 border-b border-green-200' : 'bg-cyan-50 border-b border-cyan-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Power size={18} className={isAutoReadOn ? 'text-green-600' : 'text-gray-400'} />
                    <span className="text-sm font-medium text-charcoal">Auto-Read Mode</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPlaying && isAutoReadOn && (
                      <button
                        onClick={stopReading}
                        className="p-1.5 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                        title="Stop reading"
                      >
                        <StopCircle size={16} />
                      </button>
                    )}
                    <button
                      onClick={toggleAutoRead}
                      className={`relative w-12 h-6 rounded-full transition-colors ${isAutoReadOn ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <motion.div
                        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow"
                        animate={{ left: isAutoReadOn ? 28 : 4 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    </button>
                  </div>
                </div>
                {isAutoReadOn && (
                  <p className="text-xs text-green-600 mt-1">
                    I'll automatically read each page as you navigate!
                  </p>
                )}
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-gold/20">
                <button
                  onClick={() => setActiveTab('translate')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'translate' 
                      ? `${isAutoReadOn ? 'text-green-600 border-b-2 border-green-500' : 'text-cyan-600 border-b-2 border-cyan-500'}` 
                      : 'text-charcoal/60 hover:text-charcoal'
                  }`}
                >
                  <Languages size={16} className="inline mr-1" /> Translate
                </button>
                <button
                  onClick={() => setActiveTab('read')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'read' 
                      ? `${isAutoReadOn ? 'text-green-600 border-b-2 border-green-500' : 'text-cyan-600 border-b-2 border-cyan-500'}` 
                      : 'text-charcoal/60 hover:text-charcoal'
                  }`}
                >
                  <BookOpen size={16} className="inline mr-1" /> Read Aloud
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'settings' 
                      ? `${isAutoReadOn ? 'text-green-600 border-b-2 border-green-500' : 'text-cyan-600 border-b-2 border-cyan-500'}` 
                      : 'text-charcoal/60 hover:text-charcoal'
                  }`}
                >
                  <Settings size={16} className="inline mr-1" /> Settings
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Translate Tab */}
                {activeTab === 'translate' && (
                  <div className="space-y-4">
                    {/* Language Selector */}
                    <div className="relative">
                      <label className="text-xs text-charcoal/60 mb-1 block">Translate to:</label>
                      <button
                        onClick={() => setShowLangDropdown(!showLangDropdown)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 bg-cream rounded-xl border ${isAutoReadOn ? 'border-green-200 hover:border-green-400' : 'border-gold/20 hover:border-cyan-400/40'} transition-colors`}
                      >
                        <span className="flex items-center gap-2">
                          <span>{LANGUAGES.find(l => l.code === targetLanguage)?.flag}</span>
                          <span>{LANGUAGES.find(l => l.code === targetLanguage)?.name}</span>
                        </span>
                        <ChevronDown size={16} className={`transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showLangDropdown && (
                        <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gold/20 max-h-48 overflow-y-auto">
                          {LANGUAGES.map(lang => (
                            <button
                              key={lang.code}
                              onClick={() => {
                                setTargetLanguage(lang.code);
                                setShowLangDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2 px-4 py-2 hover:bg-cyan-50 transition-colors ${
                                targetLanguage === lang.code ? 'bg-cyan-50' : ''
                              }`}
                            >
                              <span>{lang.flag}</span>
                              <span className="text-sm">{lang.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Input Area */}
                    <div>
                      <label className="text-xs text-charcoal/60 mb-1 block">Enter text or speak:</label>
                      <textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Type or speak something to translate..."
                        className={`w-full h-24 px-4 py-3 bg-cream rounded-xl border ${isAutoReadOn ? 'border-green-200 focus:border-green-400' : 'border-gold/20 focus:border-cyan-400'} focus:outline-none resize-none text-sm`}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={toggleListening}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium transition-colors ${
                          isListening 
                            ? 'bg-red-500 text-white' 
                            : `bg-cream text-charcoal ${isAutoReadOn ? 'hover:bg-green-50' : 'hover:bg-cyan-50'}`
                        }`}
                      >
                        {isListening ? <MicOff size={18} /> : <Mic size={18} />}
                        {isListening ? 'Stop' : 'Speak'}
                      </button>
                      
                      <button
                        onClick={translateAndSpeak}
                        disabled={isProcessing || !inputText.trim()}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 ${isAutoReadOn ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' : 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700'} text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isProcessing ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Volume2 size={18} />
                        )}
                        Translate & Speak
                      </button>
                    </div>

                    {/* Translated Result */}
                    {translatedText && (
                      <div className={`${isAutoReadOn ? 'bg-green-50 border-green-200' : 'bg-cyan-50 border-cyan-200'} rounded-xl p-4 border`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs ${isAutoReadOn ? 'text-green-600' : 'text-cyan-600'} font-medium`}>Translation:</span>
                          <button
                            onClick={togglePlayback}
                            className={`p-1.5 ${isAutoReadOn ? 'bg-green-100 hover:bg-green-200' : 'bg-cyan-100 hover:bg-cyan-200'} rounded-full transition-colors`}
                          >
                            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                        </div>
                        <p className="text-charcoal text-sm">{translatedText}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Read Aloud Tab */}
                {activeTab === 'read' && (
                  <div className="space-y-4">
                    <div className={`${isAutoReadOn ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-100' : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-100'} rounded-xl p-4 text-center border`}>
                      <Headphones size={32} className={`mx-auto ${isAutoReadOn ? 'text-green-500' : 'text-cyan-500'} mb-2`} />
                      <h4 className="font-medium text-charcoal mb-1">Read Aloud Feature</h4>
                      <p className="text-xs text-charcoal/60 mb-4">
                        Select any text on the page, then click the button below to have it read aloud.
                      </p>
                      
                      <button
                        onClick={readSelectedText}
                        disabled={isProcessing}
                        className={`w-full flex items-center justify-center gap-2 py-3 ${isAutoReadOn ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' : 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700'} text-white rounded-xl font-medium transition-colors disabled:opacity-50`}
                      >
                        {isProcessing ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Volume2 size={18} />
                        )}
                        Read Selected Text
                      </button>
                    </div>

                    <div className="text-center">
                      <p className="text-xs text-charcoal/50">Or enter text manually:</p>
                    </div>

                    <textarea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Paste or type text here to read aloud..."
                      className={`w-full h-20 px-4 py-3 bg-cream rounded-xl border ${isAutoReadOn ? 'border-green-200 focus:border-green-400' : 'border-gold/20 focus:border-cyan-400'} focus:outline-none resize-none text-sm`}
                    />

                    <button
                      onClick={() => readAloud(inputText)}
                      disabled={isProcessing || !inputText.trim()}
                      className={`w-full flex items-center justify-center gap-2 py-3 ${isAutoReadOn ? 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700' : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700'} text-white rounded-xl font-medium transition-colors disabled:opacity-50`}
                    >
                      {isProcessing ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <BookOpen size={18} />
                      )}
                      Read This Text
                    </button>
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-charcoal/60 mb-2 block">Voice Speed</label>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-charcoal/50">Slow</span>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.25"
                          value={audioSpeed}
                          onChange={(e) => setAudioSpeed(parseFloat(e.target.value))}
                          className={`flex-1 ${isAutoReadOn ? 'accent-green-500' : 'accent-cyan-500'}`}
                        />
                        <span className="text-xs text-charcoal/50">Fast</span>
                      </div>
                      <p className="text-center text-sm text-charcoal mt-1">{audioSpeed}x</p>
                    </div>

                    <div className={`${isAutoReadOn ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-100' : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-100'} rounded-xl p-4 border`}>
                      <h4 className="font-medium text-charcoal mb-2 text-sm">Tips:</h4>
                      <ul className="text-xs text-charcoal/70 space-y-1">
                        <li>• <strong>Auto-Read Mode:</strong> Turn on to read every page automatically</li>
                        <li>• Use "Translate" to convert text to another language</li>
                        <li>• Use "Read Aloud" for selected text</li>
                        <li>• Adjust speed for easier listening</li>
                      </ul>
                    </div>

                    <div className="text-center text-xs text-charcoal/50">
                      <p>Powered by AI 🤖</p>
                      <p className={isAutoReadOn ? 'text-green-500' : 'text-cyan-500'}>Uses Emergent Universal Key</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
